import { useState } from 'react';
import { processAttendanceFile } from '../../utils/attendanceParser';
import { analyzeWeeklyAttendance } from '../../utils/attendanceLogic';
import { collection, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { toast } from 'react-toastify';

export const WeeklyAttendanceUpload = () => {
    const [file, setFile] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);

    const handlePreview = async () => {
        if (!file || !startDate || !endDate) return toast.warning("Selecciona archivo y fechas.");
        
        setLoading(true);
        try {
            const rawLogs = await processAttendanceFile(file);
            console.log("Logs crudos:", rawLogs.length);
            const usersSnap = await getDocs(collection(db, "usuarios"));
            const employees = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            const permSnap = await getDocs(collection(db, "solicitudesVacaciones"));
            const permissions = permSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            const report = analyzeWeeklyAttendance(rawLogs, employees, permissions, startDate, endDate);
            setPreviewData(report);
            toast.success("Cálculo completado. Revisa la tabla.");

        } catch (error) {
            console.error(error);
            toast.error("Error al analizar datos.");
        } finally {
            setLoading(false);
        }
    };

    const handlePublish = async () => {
        if (!previewData) return;
        const confirm = window.confirm("¿Publicar este reporte? Los empleados recibirán notificación.");
        if (!confirm) return;

        setLoading(true);
        try {
            const batchPromises = previewData.map(async (empReport) => {
                await addDoc(collection(db, "reportesSemanales"), {
                    uid: empReport.uid,
                    nombre: empReport.nombre,
                    semanaInicio: startDate,
                    semanaFin: endDate,
                    fechaGenerado: Timestamp.now(),
                    resumen: empReport.resumen,
                    detalleDias: empReport.dias,
                    estadoFirma: 'Pendiente',
                });
            });

            await Promise.all(batchPromises);
            toast.success("✅ Reportes enviados a los empleados.");
            setPreviewData(null);

        } catch (error) {
            toast.error("Error al guardar.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 items-end">
                <div>
                    <label className="block text-sm font-bold text-gray-600">Inicio Semana (Lun)</label>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full border p-2 rounded"/>
                </div>
                <div>
                    <label className="block text-sm font-bold text-gray-600">Fin Semana (Dom)</label>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full border p-2 rounded"/>
                </div>
                <div>
                    <label className="block text-sm font-bold text-gray-600">Archivo USB (.txt)</label>
                    <input type="file" accept=".txt" onChange={e => setFile(e.target.files[0])} className="w-full text-sm"/>
                </div>
                <button 
                    onClick={handlePreview} 
                    disabled={loading}
                    className="bg-blue-600 text-white font-bold py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                    {loading ? 'Procesando...' : '🔍 Analizar'}
                </button>
            </div>

            {previewData && (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm border-collapse">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="border p-2 text-left">Empleado</th>
                                <th className="border p-2">Resumen</th>
                                <th className="border p-2 text-center">Detalle Semanal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {previewData.map((emp) => (
                                <tr key={emp.uid} className="border-b hover:bg-gray-50">
                                    <td className="border p-2 font-medium">
                                        {emp.nombre} <br/>
                                        <span className="text-xs text-gray-500">ID: {emp.idReloj}</span>
                                    </td>
                                    
                                    <td className="border p-2 min-w-[150px]">
                                        <div className="flex flex-col gap-1">
                                            <div className="flex flex-wrap gap-1">
                                                {emp.resumen.retardos > 0 && <span className="bg-red-100 text-red-800 px-2 rounded text-xs font-bold">Ret: {emp.resumen.retardos}</span>}
                                                {emp.resumen.faltas > 0 && <span className="bg-red-200 text-red-900 px-2 rounded text-xs font-bold">Faltas: {emp.resumen.faltas}</span>}
                                                {emp.resumen.sinSalida > 0 && <span className="bg-orange-100 text-orange-800 px-2 rounded text-xs font-bold">Sin Salida: {emp.resumen.sinSalida}</span>}
                                            </div>
                                            
                                            {emp.resumen.totalHorasExtra > 0 && (
                                                <div className="mt-1">
                                                    <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold shadow-sm">
                                                        ⭐ {emp.resumen.totalHorasExtra} Hrs Extra
                                                    </span>
                                                </div>
                                            )}
                                            
                                            {emp.resumen.incidencias === 0 && emp.resumen.totalHorasExtra === 0 && (
                                                <span className="text-green-600 text-xs font-bold">✅ Sin Incidencias</span>
                                            )}
                                        </div>
                                    </td>

                                    <td className="border p-2">
                                        <div className="flex gap-1 justify-start overflow-x-auto pb-2">
                                            {emp.dias.map((dia, idx) => (
                                                <div key={idx} className={`text-center min-w-[70px] p-1 rounded ${dia.horasExtra > 0 ? 'bg-blue-50 border border-blue-200' : ''}`}>
                                                    
                                                    <div className="text-[10px] text-gray-500 font-bold uppercase">
                                                        {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'][dia.diaSemana]} {dia.fecha.slice(8)}
                                                    </div>

                                                    <div className={`text-lg font-bold ${dia.color}`} title={dia.estatus}>
                                                        {dia.horasExtra > 0 ? '⭐' : 
                                                         (dia.estatus.includes('Asistencia') || dia.estatus.includes('Llegada Tarde')) ? '✅' : 
                                                         dia.estatus.includes('Falta') ? '❌' : 
                                                         dia.estatus.includes('Retardo') ? '⚠️' : 
                                                         dia.estatus.includes('Descanso') ? '💤' : '❓'}
                                                    </div>

                                                    <div className="text-[10px] leading-tight">
                                                        <div>{dia.entrada}</div>
                                                        <div className="text-gray-400">-</div>
                                                        <div>{dia.salida}</div>
                                                    </div>

                                                    {dia.horasExtra > 0 && (
                                                        <div className="mt-1">
                                                            <span className="bg-blue-600 text-white text-[9px] px-1 rounded-full font-bold">
                                                                +{dia.horasExtra}h
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="mt-6 flex justify-end">
                        <button 
                            onClick={handlePublish}
                            className="bg-green-600 text-white font-bold py-3 px-6 rounded shadow-lg hover:bg-green-700 animate-pulse"
                        >
                            📤 Publicar y Notificar a Empleados
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};